import { apiClient } from "./apiConfig";
import { SESSION_ID } from "./apiConfig";
import { CACHE_DURATION_MS } from "../constants/gameMasterConstants";

// Cache for contest info to prevent duplicate API calls
const contestInfoCache = new Map();
// Track ongoing requests to prevent duplicate simultaneous calls
const ongoingRequests = new Map();

/**
 * API service for authentication functionality
 */
export const gameMasterAPI = {
	getGroupChatMessages: async (
		contestID = 0,
		type = "single",
		partyID = 0,
		MemberID = 0
	) => {
		const response = await apiClient.get(`/getChatMessages/`, {
			params: {
				sessionid: SESSION_ID(),
				contestID,
				type,
				partyID,
				memberID: MemberID,
			},
		});
		return response.data;
	},

	getSingleChatMessages: async (receiver) => {
		const params = {
			sessionid: SESSION_ID(),
			recieverID: receiver,
		};

		const response = await apiClient.get(`/getSingleChatMessages/`, { params });
		return response.data;
	},

	SendMessage: async (groupID, chatmessage) => {
		const response = await apiClient.post(`/sendGroupChatMessage/`, {
			sessionid: SESSION_ID(),
			groupid: groupID,
			chatmessage,
		});
		return response.data;
	},

	getJudges: async (contestID) => {
		const response = await apiClient.get(`/getjudgesforcontest/`, {
			params: {
				contestid: contestID,
			},
		});
		return response;
	},

	getContestMembers: async (contestID) => {
		const response = await apiClient.get(`/getmembersforcontest/`, {
			params: {
				contestid: contestID,
			},
		});
		return response;
	},

	getContestInfo: async (contestID) => {
		// Check cache first
		const cacheKey = `contest_${contestID}`;
		const cached = contestInfoCache.get(cacheKey);
		if (cached && Date.now() - cached.timestamp < CACHE_DURATION_MS) {
			return cached.data;
		}

		// Check if there's already an ongoing request for this contest
		const ongoingRequest = ongoingRequests.get(cacheKey);
		if (ongoingRequest) {
			return await ongoingRequest;
		}

		// Create a promise for this request and store it
		const requestPromise = apiClient
			.get(`/getcontestinfo/`, {
				params: {
					contestid: contestID,
				},
			})
			.then((response) => {
				// Cache the result
				contestInfoCache.set(cacheKey, {
					data: response.data,
					timestamp: Date.now(),
				});
				// Remove from ongoing requests
				ongoingRequests.delete(cacheKey);
				return response.data;
			})
			.catch((error) => {
				// Remove from ongoing requests even on error
				ongoingRequests.delete(cacheKey);
				throw error;
			});

		// Store the ongoing request
		ongoingRequests.set(cacheKey, requestPromise);

		return await requestPromise;
	},
	postJudgement: async (
		queueItemID,
		contestID,
		lumens = 0,
		score = 0,
		comment = "",
		approve = 1
	) => {
		const response = await apiClient.post(`/postjudgement/`, {
			sessionid: SESSION_ID(),
			contestid: contestID,
			comment,
			queueitemid: queueItemID,
			score,
			lumens,
			approve,
		});
		return response.data;
	},

	getJudgeQueue: async (contestID, MemberID, index) => {
		const response = await apiClient.get(`/getjudgingqueue/`, {
			params: {
				contestid: contestID,
				memberid: MemberID,
				index,
			},
		});
		return response.data;
	},

	getNextJudgeQueueItem: async (
		contestID,
		currentQueueItemID = 0,
		direction = "next"
	) => {
		const response = await apiClient.get(`/getnextinjudgequeue/`, {
			params: {
				sessionid: SESSION_ID(),
				contestid: contestID,
				postid: currentQueueItemID,
				direction,
			},
		});
		return response.data;
	},

	getContestAgenda: async (contestID, date) => {
		const response = await apiClient.post(`/getcontestagenda/`, {
			contestid: contestID,
			currenttime: date,
		});
		return response.data;
	},

	createContestAgendaItem: async (data) => {
		const response = await apiClient.post(`/createcontestagendaitem/`, data);
		return response.data;
	},

	updateContestAgendaItem: async (data) => {
		const response = await apiClient.post(`/updatecontestagendaitem/`, data);
		return response.data;
	},

	deleteContestAgendaItem: async (AgendaID) => {
		const response = await apiClient.post(`/deletecontestagendaitem/`, {
			id: AgendaID,
		});
		return response.data;
	},

	// TESTING ROUTE TODO: REMOVE
	populateContestAgenda: async (contestID) => {
		console.log("Populating agenda items!!!");
		const response = await apiClient.post(`/populatecontestagenda/`, {
			contestid: contestID,
		});
		return response.data;
	},

	getDashboard: async (contestID) => {
		const response = await apiClient.get(
			`/getdashboard/?contestID=${contestID}`
		);
		return response.data;
	},

	getInviteStatus: async (contestID) => {
		const response = await apiClient.get(
			`/getinvitestatus/?contestID=${contestID}`
		);
		return response.data;
	},

	addUserRole: async (contestID, roleType, MemberID = 0) => {
		const response = await apiClient.post(`/acceptinvite/`, {
			sessionid: SESSION_ID(),
			contestid: contestID,
			role: roleType,
			memberid: MemberID,
		});
		return response.data;
	},

	changeContestStatus: async (contestID, status) => {
		const response = await apiClient.post(`/changeconteststatus/`, {
			sessionid: SESSION_ID(),
			contestID,
			status,
		});
		return response.data;
	},

	createContest: async (contestName, MemberID) => {
		const response = await apiClient.post(`/createcontest/`, {
			memberid: MemberID,
			contestname: contestName,
		});
		return response.data;
	},
};
